import { authService } from './authService';

// Deduplicate concurrent refresh calls: all requests that hit 401 simultaneously
// share the same refresh promise instead of each firing their own /api/auth/refresh.
let refreshPromise = null;

const tryRefreshOnce = () => {
  if (!refreshPromise) {
    refreshPromise = authService.refreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

export const apiClient = {
  /**
   * Make API request with authentication.
   * In development, requests use relative paths (e.g. /api/chatbots) so the
   * CRA dev proxy in setupProxy.js forwards them to the correct backend port.
   * Cookies are included automatically — no cross-origin issues.
   *
   * On 401: attempts a single silent token refresh, then retries the request.
   * If the refresh also fails the user is logged out automatically.
   */
  async request(endpoint, options = {}, _isRetry = false) {
    const companyId = authService.getCompanyId();

    const config = {
      ...options,
      credentials: 'include', // send cookies on every request
      headers: {
        'Content-Type': 'application/json',
        ...(companyId && { 'X-Company-Id': companyId }),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(endpoint, config);

      // Handle 401 Unauthorized
      if (response.status === 401) {
        if (_isRetry) {
          // Refresh already attempted — session is truly expired, logout
          console.warn('Session expired — logging out');
          authService.logout();
          return;
        }
        // Try to silently refresh, then replay the original request once
        try {
          await tryRefreshOnce();
          return this.request(endpoint, options, true);
        } catch {
          console.warn('Token refresh failed — logging out');
          authService.logout();
          return;
        }
      }

      // Handle other error statuses
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || `Request failed with status ${response.status}`);
      }

      // Return parsed JSON, or null for 204 / empty body
      if (response.status === 204) {
        return null;
      }

      const text = await response.text();
      if (!text) return null;
      return JSON.parse(text);
    } catch (err) {
      console.error('API Error:', err);
      throw err;
    }
  },

  /**
   * GET request
   */
  get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  },

  /**
   * POST request
   */
  post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * PUT request
   */
  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * PATCH request
   */
  patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * DELETE request
   */
  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  },

  /**
   * Upload file (multipart/form-data)
   * Cookies sent automatically with credentials: 'include'
   * Same silent-refresh-on-401 behaviour as request().
   */
  async upload(endpoint, formData, _isRetry = false) {
    const companyId = authService.getCompanyId();

    const config = {
      method: 'POST',
      credentials: 'include',
      headers: {
        // Don't set Content-Type for FormData - browser sets it with boundary
        ...(companyId && { 'X-Company-Id': companyId }),
      },
      body: formData,
    };

    try {
      const response = await fetch(endpoint, config);

      if (response.status === 401) {
        if (_isRetry) {
          console.warn('Session expired — logging out');
          authService.logout();
          return;
        }
        try {
          await tryRefreshOnce();
          return this.upload(endpoint, formData, true);
        } catch {
          console.warn('Token refresh failed — logging out');
          authService.logout();
          return;
        }
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Upload failed');
      }

      return await response.json();
    } catch (err) {
      console.error('Upload Error:', err);
      throw err;
    }
  },
};
